import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';

const normalize = (img) =>
  typeof img === 'string' ? { src: img, caption: '', description: '' } : img;

const BlurCarousel = ({ images: rawImages }) => {
  const images = rawImages.map(normalize);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);

  const current = images[currentIndex];

  useEffect(() => {
    setCaptionExpanded(false);
  }, [currentIndex]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1 === images.length ? 0 : prev + 1));
      }
      if (e.key === 'ArrowLeft') {
        setDirection(-1);
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, images.length]);

  const slideVariants = {
    hiddenRight: { x: '100%', opacity: 0 },
    hiddenLeft: { x: '-100%', opacity: 0 },
    visible: { x: '0', opacity: 1, transition: { duration: 0.5 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1 === images.length ? 0 : prev + 1));
  };

  const handlePrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleDotClick = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  return (
    <>
      <div className="relative w-full h-[500px] lg:h-[600px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40 z-10 pointer-events-none" />

        <div className="relative w-full h-full flex items-center justify-center">
          {/* Left blurred image */}
          <motion.div
            key={`left-${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            className="absolute left-0 w-1/3 h-3/4 overflow-hidden rounded-r-3xl"
            style={{ filter: 'blur(8px)' }}
          >
            <img
              src={images[(currentIndex - 1 + images.length) % images.length].src}
              alt="Previous"
              className="w-full h-full object-cover scale-110"
            />
          </motion.div>

          {/* Center main image */}
          <div className="relative w-full max-w-4xl h-full px-4 flex items-center justify-center z-20">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial={direction > 0 ? 'hiddenRight' : 'hiddenLeft'}
                animate="visible"
                exit="exit"
                className="absolute w-full h-5/6 rounded-3xl overflow-hidden shadow-2xl border-2 border-white/10 group cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={current.src}
                  alt={current.caption || `Slide ${currentIndex + 1}`}
                  className="w-full h-full object-contain bg-[#0a0a0a]"
                  draggable="false"
                />

                {/* Zoom hint on hover */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-2xl">
                    <Maximize2 className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Caption overlay */}
                {current.caption && (
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-16 px-6 pb-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                          <p className="text-white font-bold font-silkscreen text-base lg:text-xl tracking-wide">
                            {current.caption}
                          </p>
                        </div>
                        {current.description && (
                          <div
                            className="text-white/80 text-sm lg:text-base leading-snug overflow-hidden transition-all duration-500"
                            style={{ maxHeight: captionExpanded ? '200px' : '1.4rem' }}
                          >
                            {current.description}
                          </div>
                        )}
                      </div>
                      {current.description && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCaptionExpanded(!captionExpanded); }}
                          className="flex-shrink-0 text-orange-400 text-sm font-bold hover:text-orange-300 transition-colors font-silkscreen mt-1 px-3 py-1 rounded-full border border-orange-500/40 hover:border-orange-400/60 bg-orange-500/15 hover:bg-orange-500/25"
                        >
                          {captionExpanded ? 'less ↑' : 'more...'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right blurred image */}
          <motion.div
            key={`right-${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            className="absolute right-0 w-1/3 h-3/4 overflow-hidden rounded-l-3xl"
            style={{ filter: 'blur(8px)' }}
          >
            <img
              src={images[(currentIndex + 1) % images.length].src}
              alt="Next"
              className="w-full h-full object-cover scale-110"
            />
          </motion.div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={handlePrevious}
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 group"
          aria-label="Previous image"
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-full p-3 lg:p-4 hover:bg-white/20 transition-all"
          >
            <ChevronLeft className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
          </motion.div>
        </button>

        <button
          onClick={handleNext}
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 group"
          aria-label="Next image"
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-full p-3 lg:p-4 hover:bg-white/20 transition-all"
          >
            <ChevronRight className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
          </motion.div>
        </button>

        {/* Dots Navigation */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className="group"
              aria-label={`Go to slide ${index + 1}`}
            >
              <motion.div
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentIndex === index ? 'w-8 bg-orange-500' : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Image counter */}
        <div className="absolute top-8 right-8 z-30 bg-black/50 backdrop-blur-xl border border-white/10 px-4 py-2 rounded-full">
          <span className="text-white font-silkscreen text-sm">
            {currentIndex + 1} / {images.length}
          </span>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/97 backdrop-blur-2xl flex items-center justify-center p-4 lg:p-8"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Left arrow */}
            <button
              className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full p-3 lg:p-4 transition-all"
              onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            {/* Right arrow */}
            <button
              className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full p-3 lg:p-4 transition-all"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Close button */}
            <button
              className="absolute top-4 right-4 lg:top-6 lg:right-6 z-10 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full p-2.5 transition-all"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Content */}
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="relative flex flex-col items-center gap-5 max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  src={current.src}
                  alt={current.caption || `Slide ${currentIndex + 1}`}
                  className="max-h-[65vh] max-w-full object-contain rounded-2xl shadow-2xl border border-white/10"
                  draggable="false"
                />
              </AnimatePresence>

              {current.caption && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`caption-${currentIndex}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="text-center max-w-2xl px-4"
                  >
                    <p className="text-white font-bold font-silkscreen text-base lg:text-lg mb-2">
                      {current.caption}
                    </p>
                    {current.description && (
                      <p className="text-white/60 text-sm leading-relaxed">
                        {current.description}
                      </p>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}

              <div className="bg-white/10 border border-white/10 px-4 py-1.5 rounded-full">
                <span className="text-white/50 font-silkscreen text-xs">
                  {currentIndex + 1} / {images.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BlurCarousel;
